import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { 
  Activity, Shield, AlertTriangle, CheckCircle, Loader2, 
  ExternalLink, RefreshCw, LogOut, Zap, BarChart3, Clock,
  Key, Eye, EyeOff, Settings, Trash2, Copy, ArrowLeft, Link2, PlayCircle, Youtube
} from "lucide-react";
import { apiClient } from "../../App";
import { toast } from "sonner";

const ConnectZerodha = ({ user, onConnected, onLogout }) => {
  const [loading, setLoading] = useState(false);
  const [kiteStatus, setKiteStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [callbackProcessed, setCallbackProcessed] = useState(false);
  
  // Credentials form state
  const [showCredentialsForm, setShowCredentialsForm] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [savingCredentials, setSavingCredentials] = useState(false);
  
  // Tutorial video link
  const [tutorialVideoUrl, setTutorialVideoUrl] = useState("");
  
  // Generate correct redirect URL for user's Kite app - MUST match Kite Developer settings
  const redirectUrl = `${window.location.origin}/api/auth/zerodha/callback`;

  useEffect(() => {
    checkKiteStatus();
    fetchTutorialVideo();
    
    // Check for callback from Zerodha (request_token in URL)
    // GLOBAL single-use lock: only ONE handler in the whole app processes a given request_token
    // (prevents race with Dashboard.jsx which also watches this param)
    const urlParams = new URLSearchParams(window.location.search);
    const requestToken = urlParams.get('request_token');
    if (requestToken && !callbackProcessed) {
      const claimedKey = `zerodha_token_claimed_${requestToken}`;
      if (!sessionStorage.getItem(claimedKey)) {
        sessionStorage.setItem(claimedKey, '1');
        setCallbackProcessed(true);
        handleKiteCallback(requestToken);
      } else {
        // Another component already claimed this token - mark processed silently
        setCallbackProcessed(true);
      }
    }
  }, [callbackProcessed]);

  // Fetch tutorial video URL from settings
  const fetchTutorialVideo = async () => {
    try {
      const response = await apiClient.get('/payment/settings');
      if (response.data.zerodha_setup_video) {
        setTutorialVideoUrl(response.data.zerodha_setup_video);
      }
    } catch (error) {
      console.error('Failed to fetch tutorial video:', error);
    }
  };

  const checkKiteStatus = async () => {
    setCheckingStatus(true);
    try {
      const response = await apiClient.get('/user/kite/status');
      setKiteStatus(response.data);
      
      // If connected and valid, proceed to app
      if (response.data.connected && !response.data.needs_daily_auth) {
        onConnected && onConnected(response.data);
      }
      
      // If has credentials but needs auth, show login button
      if (response.data.has_credentials && response.data.needs_daily_auth) {
        setShowCredentialsForm(false);
      }
      
      // If no credentials, show form
      if (!response.data.has_credentials) {
        setShowCredentialsForm(true);
      }
    } catch (err) {
      console.error('Failed to check Kite status:', err);
      setShowCredentialsForm(true);
    }
    setCheckingStatus(false);
  };

  const copyToClipboard = (text, label) => {
    // Try modern clipboard API first, fallback to execCommand
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => toast.success(`${label} copied to clipboard!`))
        .catch(() => fallbackCopyToClipboard(text, label));
    } else {
      fallbackCopyToClipboard(text, label);
    }
  };

  const fallbackCopyToClipboard = (text, label) => {
    // Fallback method using textarea and execCommand
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      toast.success(`${label} copied to clipboard!`);
    } catch (err) {
      // If all else fails, show the text in an alert for manual copy
      toast.info(`Please copy manually: ${text}`);
    }
    
    document.body.removeChild(textArea);
  };

  const saveCredentials = async () => {
    if (!apiKey.trim() || !apiSecret.trim()) {
      toast.error('Please enter both API Key and API Secret');
      return;
    }
    
    setSavingCredentials(true);
    try {
      const response = await apiClient.post('/user/kite/save-credentials', {
        api_key: apiKey.trim(),
        api_secret: apiSecret.trim(),
        redirect_url: redirectUrl
      });
      
      if (response.data.success) {
        toast.success('Credentials saved securely!');
        setShowCredentialsForm(false);
        setApiKey("");
        setApiSecret("");
        await checkKiteStatus();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save credentials');
    }
    setSavingCredentials(false);
  };

  const handleConnectZerodha = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/user/kite/login-url');
      if (response.data.success) {
        // Store state for verification after redirect
        localStorage.setItem('kite_oauth_state', response.data.state);
        // IMPORTANT: Pre-set active broker to zerodha before redirect
        // This ensures that when user returns from OAuth, the app knows it was Zerodha
        localStorage.setItem('active_broker', 'zerodha');
        // Redirect to Zerodha login
        window.location.href = response.data.login_url;
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to get login URL';
      toast.error(message);
      
      // If credentials not saved, show form
      if (message.includes('credentials')) {
        setShowCredentialsForm(true);
      }
    }
    setLoading(false);
  };

  const handleKiteCallback = async (requestToken) => {
    setLoading(true);
    try {
      const response = await apiClient.post(`/user/kite/callback?request_token=${requestToken}`);
      if (response.data.success) {
        toast.success('Zerodha authenticated! Valid for 24 hours.');
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // IMPORTANT: Set active broker to zerodha in localStorage
        localStorage.setItem('active_broker', 'zerodha');
        
        // Refresh status
        await checkKiteStatus();
        // Pass broker info to onConnected
        onConnected && onConnected({ ...response.data, broker: 'zerodha' });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to authenticate with Zerodha');
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    setLoading(false);
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your Zerodha session?')) return;
    
    try {
      await apiClient.post('/user/kite/disconnect');
      toast.success('Session disconnected');
      setKiteStatus(prev => ({ ...prev, connected: false, needs_daily_auth: true }));
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  const handleDeleteCredentials = async () => {
    if (!window.confirm('Delete your saved Kite credentials? You will need to enter them again.')) return;
    
    try {
      await apiClient.delete('/user/kite/credentials');
      toast.success('Credentials deleted');
      setShowCredentialsForm(true);
      setKiteStatus(null);
    } catch (err) {
      toast.error('Failed to delete credentials');
    }
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      // Fallback logout
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/';
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-[#030508] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  // If connected with valid session, show status card
  if (kiteStatus?.connected && !kiteStatus?.needs_daily_auth) {
    return (
      <div className="fixed inset-0 z-50 bg-[#030508]/95 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-[#0a1628] to-[#061020] border border-green-500/30 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 border-2 border-green-500/50 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-2">Zerodha Connected!</h2>
          <p className="text-[#6b7a99] mb-2">
            Logged in as <span className="text-cyan-400 font-semibold">{kiteStatus.kite_user_name || kiteStatus.kite_user_id}</span>
          </p>
          
          <div className="bg-[#0a0e17] rounded-lg p-4 mb-6">
            <p className="text-xs text-green-400 font-medium mb-1">
              Session valid for {kiteStatus.hours_remaining || 0} more hours
            </p>
            <p className="text-[10px] text-[#6b7a99]">
              Expires: {kiteStatus.token_expiry ? new Date(kiteStatus.token_expiry).toLocaleString() : 'N/A'}
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={() => onConnected && onConnected(kiteStatus)}
              className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
            >
              Continue to App
            </Button>
            <Button
              onClick={handleDisconnect}
              variant="outline"
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-[#030508] flex items-center justify-center p-4 overflow-y-auto">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-cyan-500/10 via-blue-600/10 to-purple-500/10 rounded-full blur-[100px]" />
      </div>
      
      <div className="relative z-10 max-w-lg w-full">
        {/* Back/Logout Button */}
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-[#6b7a99] hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </button>
          
          <span className="text-xs text-[#4a5a7a]">
            {user?.email}
          </span>
        </div>
        
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-[#0a1628] to-[#061020] border-2 border-cyan-400/50 flex items-center justify-center">
            <Activity className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">
            {showCredentialsForm ? 'Setup Kite Connect' : 'Daily Zerodha Login'}
          </h1>
          <p className="text-sm text-[#6b7a99]">
            {showCredentialsForm 
              ? 'Enter your Kite Connect API credentials' 
              : 'Authenticate with Zerodha (required every 24 hours)'}
          </p>
          
          {/* Tutorial Video Link - Always Visible */}
          {tutorialVideoUrl && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <a 
                href={tutorialVideoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-colors"
              >
                <Youtube className="w-5 h-5 text-red-500" />
                <span className="text-sm text-red-400 font-medium">Watch Setup Tutorial</span>
                <ExternalLink className="w-4 h-4 text-red-400" />
              </a>
              <button
                onClick={() => {
                  // Fallback copy method using textarea
                  const textArea = document.createElement("textarea");
                  textArea.value = tutorialVideoUrl;
                  textArea.style.position = "fixed";
                  textArea.style.left = "-999999px";
                  textArea.style.top = "-999999px";
                  document.body.appendChild(textArea);
                  textArea.focus();
                  textArea.select();
                  try {
                    document.execCommand('copy');
                    toast.success('Video link copied!');
                  } catch (err) {
                    toast.error('Failed to copy link');
                  }
                  document.body.removeChild(textArea);
                }}
                className="flex items-center gap-1.5 px-3 py-2 bg-[#1a2540] border border-[#2a3550] rounded-lg hover:bg-[#2a3550] transition-colors"
                title="Copy video link"
              >
                <Copy className="w-4 h-4 text-cyan-400" />
                <span className="text-xs text-cyan-400">Copy Link</span>
              </button>
            </div>
          )}
        </div>
        
        {/* Credentials Form */}
        {showCredentialsForm ? (
          <div className="bg-gradient-to-br from-[#0a1628] to-[#061020] border border-[#1a2540] rounded-2xl p-6 mb-4">
            {/* Instructions */}
            <div className="flex items-start gap-3 mb-4 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
              <Key className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-cyan-400 font-medium">Your Own Kite Connect App</p>
                <p className="text-xs text-[#6b7a99] mt-1">
                  Create your app at{" "}
                  <a href="https://developers.kite.trade" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline">
                    developers.kite.trade
                  </a>
                </p>
              </div>
            </div>
            
            {/* Redirect URL - Important! */}
            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-amber-400" />
                  <span className="text-xs font-medium text-amber-400">Redirect URL (IMPORTANT!)</span>
                </div>
                <button
                  onClick={() => copyToClipboard(redirectUrl, 'Redirect URL')}
                  className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300"
                >
                  <Copy className="w-3 h-3" />
                  Copy
                </button>
              </div>
              <div className="bg-[#0a0e17] rounded p-2 font-mono text-xs text-white break-all">
                {redirectUrl}
              </div>
              <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded">
                <p className="text-[10px] text-red-400 font-medium">
                  ⚠️ CRITICAL: Copy this exact URL and paste it in your Kite Connect app's "Redirect URL" field at{" "}
                  <a 
                    href="https://developers.kite.trade/apps" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="underline hover:text-red-300"
                  >
                    developers.kite.trade
                  </a>
                </p>
                <p className="text-[9px] text-[#6b7a99] mt-1">
                  If you previously used a different URL, you must update it to this new URL for authentication to work.
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-[#6b7a99] text-xs">API Key *</Label>
                <Input
                  type="text"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Your Kite Connect API Key"
                  className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white"
                />
              </div>
              
              <div>
                <Label className="text-[#6b7a99] text-xs">API Secret *</Label>
                <div className="relative">
                  <Input
                    type={showSecret ? "text" : "password"}
                    value={apiSecret}
                    onChange={(e) => setApiSecret(e.target.value)}
                    placeholder="Your Kite Connect API Secret"
                    className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b7a99] hover:text-white"
                  >
                    {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            
            <Button
              onClick={saveCredentials}
              disabled={savingCredentials || !apiKey.trim() || !apiSecret.trim()}
              className="w-full mt-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
            >
              {savingCredentials ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Save & Continue
                </>
              )}
            </Button>
            
            <p className="text-center text-[10px] text-[#4a5a7a] mt-3">
              Your credentials are encrypted and stored securely
            </p>
            
            {/* Back to login link */}
            <div className="text-center mt-4 pt-4 border-t border-[#1a2540]">
              <button
                onClick={handleLogout}
                className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1 mx-auto"
              >
                <LogOut className="w-3 h-3" />
                Logout & Go Back
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Has credentials, needs daily auth - SIMPLIFIED UI */}
            <div className="bg-gradient-to-br from-[#0a1628] to-[#061020] border border-[#1a2540] rounded-2xl p-6 mb-4">
              
              {/* Step 1: Redirect URL - Always show prominently */}
              <div className="mb-5 p-4 bg-amber-500/10 border border-amber-500/40 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-amber-400" />
                    <span className="text-sm font-semibold text-amber-400">Your Redirect URL</span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(redirectUrl, 'Redirect URL')}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white bg-amber-500/30 hover:bg-amber-500/50 rounded-lg transition-colors"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Copy
                  </button>
                </div>
                <div className="bg-[#030508] rounded-lg p-3 font-mono text-xs text-white break-all select-all border border-amber-500/20">
                  {redirectUrl}
                </div>
                <p className="text-[10px] text-amber-300/80 mt-2">
                  Paste this in your Kite app at{" "}
                  <a 
                    href="https://developers.kite.trade/apps" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="underline hover:text-amber-200"
                  >
                    developers.kite.trade
                  </a>
                </p>
              </div>
              
              {/* Login Button - Main CTA */}
              <Button
                onClick={handleConnectZerodha}
                disabled={loading}
                className="w-full h-14 text-white text-lg font-semibold rounded-xl shadow-lg bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 shadow-orange-500/30"
                data-testid="zerodha-login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <ExternalLink className="w-5 h-5 mr-2" />
                    Login with Zerodha
                  </>
                )}
              </Button>
              
              <p className="text-center text-xs text-[#6b7a99] mt-3">
                You'll be redirected to Zerodha's secure login page
              </p>
            </div>
            
            {/* Change Credentials - Collapsed at bottom */}
            <div className="text-center">
              <button
                onClick={() => setShowCredentialsForm(true)}
                className="text-xs text-[#6b7a99] hover:text-cyan-400 flex items-center gap-1.5 mx-auto transition-colors"
              >
                <Settings className="w-3.5 h-3.5" />
                Change Credentials
              </button>
            </div>
          </>
        )}
        
        {/* Refresh status */}
        <div className="text-center mt-4">
          <button
            onClick={checkKiteStatus}
            className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 mx-auto"
          >
            <RefreshCw className="w-3 h-3" />
            Refresh Status
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConnectZerodha;
